import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flame, Clock, ArrowRight, Moon, Battery, Smile, Heart, ChevronRight, CalendarCheck, FileText, Lock, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/home").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="home-loading">
        <div className="w-10 h-10 rounded-full border-[3px] border-[var(--surface-accent)] border-t-[var(--brand)] animate-spin" />
      </div>
    );
  }

  const lesson = data.today_lesson;
  const checkin = data.latest_checkin;
  const metrics = [
    { key: "sleep", label: "Sleep", icon: Moon, value: checkin?.sleep_score },
    { key: "energy", label: "Energy", icon: Battery, value: checkin?.energy_score },
    { key: "mood", label: "Mood", icon: Smile, value: checkin?.mood_score },
    { key: "wellbeing", label: "Overall", icon: Heart, value: checkin?.wellbeing_score },
  ];

  return (
    <div className="px-5 pt-8 animate-fade-rise" data-testid="home-page">
      {/* trial banner */}
      {user?.access_state === "trial" && (
        <div className="mb-5 bg-brand rounded-2xl p-4 flex items-center justify-between gap-3" data-testid="home-trial-banner">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
              <Sparkles size={18} style={{ color: "var(--amber)" }} />
            </div>
            <div>
              <div className="text-white font-semibold text-sm">
                {user.trial_days_left} {user.trial_days_left === 1 ? "day" : "days"} left in your free trial
              </div>
              <div className="text-white/70 text-xs mt-0.5">Subscribe anytime to keep your access</div>
            </div>
          </div>
          <button
            data-testid="home-trial-subscribe"
            onClick={() => navigate("/checkout")}
            className="bg-white text-brand text-xs font-semibold px-4 py-2 rounded-full flex-shrink-0"
          >
            Subscribe
          </button>
        </div>
      )}
      {/* greeting */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-ink-muted text-sm">{greeting()},</p>
          <h1 className="font-serif text-3xl font-bold text-ink" data-testid="home-greeting">{data.first_name || "there"}</h1>
        </div>
        <div className="flex items-center gap-1.5 bg-white rounded-full px-3.5 py-2 card-shadow" data-testid="home-streak">
          <Flame size={16} className="text-accent-c" />
          <span className="font-bold text-ink text-sm">{data.streak}</span>
          <span className="text-ink-muted text-xs">day{data.streak === 1 ? "" : "s"}</span>
        </div>
      </div>

      {data.welcome_message && (
        <div className="mt-4 bg-sage rounded-2xl px-5 py-4 text-brand text-sm font-medium" data-testid="home-welcome">
          {data.welcome_message}
        </div>
      )}

      {/* progress bar */}
      <div className="mt-6 bg-white rounded-2xl p-5 card-shadow">
        <div className="flex items-center justify-between mb-2">
          <span className="font-semibold text-ink" data-testid="home-day">Day {data.current_day} of 30</span>
          <span className="text-brand font-bold text-sm">{data.completion_pct}%</span>
        </div>
        <div className="h-2.5 rounded-full bg-[var(--surface-accent)] overflow-hidden">
          <div className="h-full bg-brand transition-all duration-500" style={{ width: `${data.completion_pct}%` }} />
        </div>
        <div className="text-ink-muted text-xs mt-2">{data.completed_count} of 30 days complete</div>
      </div>

      {/* today's lesson */}
      <div className="mt-5">
        <p className="overline-label">Today's reset</p>
        <div
          className="mt-2 bg-brand rounded-3xl p-6 text-white cursor-pointer transition-transform active:scale-[0.99]"
          onClick={() => navigate(`/lesson/${lesson.day}`)}
          data-testid="today-lesson-card"
        >
          <div className="flex items-center gap-2 text-xs" style={{ color: "var(--amber)" }}>
            <span className="font-semibold">{lesson.week_title}</span>
            <span className="text-white/30">·</span>
            <span className="text-white/60">{lesson.category}</span>
          </div>
          <h2 className="font-serif text-2xl font-bold mt-2">Day {lesson.day}: {lesson.title}</h2>
          <div className="flex items-center gap-2 mt-3 text-white/60 text-sm">
            <Clock size={15} /> About {lesson.estimated_time} minutes
            {lesson.completed && <span className="ml-2 bg-white/15 rounded-full px-2 py-0.5 text-xs">Completed ✓</span>}
          </div>
          <button
            data-testid="start-today-btn"
            className="mt-5 w-full bg-white text-brand font-semibold py-3.5 rounded-full flex items-center justify-center gap-2 hover:bg-[var(--sage)] transition-colors"
          >
            {lesson.completed ? "Revisit Today's Reset" : "Start Today's Reset"} <ArrowRight size={17} />
          </button>
        </div>
      </div>

      {/* quick check in */}
      <div className="mt-6">
        <p className="overline-label">Quick check in</p>
        <div className="mt-2 bg-white rounded-2xl p-5 card-shadow">
          <div className="grid grid-cols-4 gap-2">
            {metrics.map((m) => {
              const Icon = m.icon;
              return (
                <div key={m.key} className="text-center">
                  <div className="w-11 h-11 rounded-full bg-sage flex items-center justify-center mx-auto">
                    <Icon size={18} className="text-brand" />
                  </div>
                  <div className="font-serif text-xl font-bold text-ink mt-1.5">{m.value ?? "–"}</div>
                  <div className="text-ink-muted text-[11px]">{m.label}</div>
                </div>
              );
            })}
          </div>
          <button
            data-testid="update-checkin-btn"
            onClick={() => navigate("/checkin")}
            className="mt-4 w-full border-2 border-[var(--brand)] text-brand font-semibold py-3 rounded-full hover:bg-sage transition-colors"
          >
            Update Check In
          </button>
        </div>
      </div>

      {/* recommended toolkit */}
      {data.recommended_toolkit?.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center justify-between">
            <p className="overline-label">Recommended for you</p>
            <button onClick={() => navigate("/toolkit")} className="text-brand text-sm font-semibold flex items-center gap-0.5">
              All tools <ChevronRight size={15} />
            </button>
          </div>
          <div className="mt-2 space-y-3">
            {data.recommended_toolkit.map((t) => (
              <div
                key={t.id}
                onClick={() => navigate("/toolkit")}
                data-testid={`home-tool-${t.id}`}
                className="bg-white rounded-2xl p-4 card-shadow flex items-center justify-between cursor-pointer active:scale-[0.99] transition-transform"
              >
                <div className="pr-3">
                  <div className="text-accent-c text-xs font-semibold">{t.category}</div>
                  <div className="font-semibold text-ink text-sm mt-0.5">{t.title}</div>
                  <div className="text-ink-muted text-xs mt-0.5">{t.duration}</div>
                </div>
                <ChevronRight size={18} className="text-ink-muted flex-shrink-0" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* weekly summaries */}
      {data.week_summaries?.some((w) => w.started) && (
        <div className="mt-6">
          <p className="overline-label">Weekly summaries</p>
          <div className="mt-2 space-y-3">
            {data.week_summaries.filter((w) => w.started).map((w) => (
              <button
                key={w.week}
                data-testid={`home-week-summary-${w.week}`}
                onClick={() => navigate(`/summary/${w.week}`)}
                className="w-full bg-white rounded-2xl p-4 card-shadow flex items-center justify-between cursor-pointer active:scale-[0.99] transition-transform text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-sage flex items-center justify-center flex-shrink-0">
                    <CalendarCheck size={18} className="text-brand" />
                  </div>
                  <div>
                    <div className="font-semibold text-ink text-sm">Week {w.week}: {w.title}</div>
                    <div className="text-ink-muted text-xs mt-0.5">
                      Days {w.start} to {w.end} · {w.available ? "Week complete" : "In progress"}
                    </div>
                  </div>
                </div>
                <ChevronRight size={18} className="text-ink-muted flex-shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* personal support plan */}
      <div className="mt-6">
        <p className="overline-label">Your report</p>
        <div className="mt-2 bg-white rounded-2xl p-5 card-shadow" data-testid="home-plan-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-sage flex items-center justify-center flex-shrink-0">
              {data.days_completed >= data.total_days ? (
                <FileText size={18} className="text-brand" />
              ) : (
                <Lock size={18} className="text-brand" />
              )}
            </div>
            <div className="flex-1">
              <div className="font-semibold text-ink text-sm">Personal Menopause Support Plan</div>
              <div className="text-ink-muted text-xs mt-0.5">
                {data.days_completed >= data.total_days
                  ? "Your full report is ready to view."
                  : `Produced once you complete all 30 days. ${data.days_completed} of ${data.total_days} done.`}
              </div>
            </div>
          </div>
          <button
            data-testid="home-plan-btn"
            onClick={() => navigate("/plan")}
            className={`mt-4 w-full font-semibold py-3 rounded-full transition-colors ${
              data.days_completed >= data.total_days
                ? "bg-brand text-white hover:bg-[var(--brand-hover)]"
                : "border-2 border-[var(--brand)] text-brand hover:bg-sage"
            }`}
          >
            {data.days_completed >= data.total_days ? "View my full report" : "See my plan so far"}
          </button>
        </div>
      </div>

      <p className="text-center text-ink-muted text-xs mt-8 px-4">
        This program supports your wellbeing and is not medical advice.
      </p>
    </div>
  );
}
